import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { ServiceSiteType } from 'api/api.types'
import { getActiveServiceGroups, type CityService, type ServiceGroup } from 'src/store/data.store'
import { getAppointmentSalon, type SelectedService } from 'src/store/appointment.store'
import servicesType1 from 'assets/service-type-1.svg'
import servicesType2 from 'assets/service-type-2.svg'
import servicesType3 from 'assets/service-type-3.svg'
import servicesType4 from 'assets/service-type-4.svg'
import servicesType8 from 'assets/service-type-8.svg'
import servicesType9 from 'assets/service-type-9.svg'
import servicesType12 from 'assets/service-type-12.svg'
import servicesType10 from 'assets/services-icons-no-box/brows-active.svg'
import { ServicesCategory } from './ServicesCategory'
import { ServicesSubCategory } from './ServicesSubCategory'
import { ServicesListItem } from './ServicesListItem'
import { ServiceGroupListItem } from './ServiceGroupListItem'
import styles from './ServiceList.module.scss'

type Category = {
  name: string,
  color: string,
  image: string,
} & ({ type: ServiceSiteType, subcategories?: undefined } | {
  type?: undefined,
  subcategories: Array<{ name: string, type: ServiceSiteType }>,
})

const CATEGORIES: Category[] = [
  { name: 'Комплекс', color: 'pink', image: servicesType3, type: ServiceSiteType.complex },
  { name: 'Маникюр', color: 'yellow', image: servicesType1, type: ServiceSiteType.manicure },
  { name: 'Педикюр', color: 'blue', image: servicesType2, type: ServiceSiteType.pedicure },
  {
    name: 'Здоровье и уход',
    color: 'satin',
    image: servicesType4,
    subcategories: [
      { name: 'Комплексный уход', type: ServiceSiteType.complexCare },
      { name: 'Увлажнение и питание', type: ServiceSiteType.moisturizing },
      { name: 'Укрепление ногтей', type: ServiceSiteType.strengthening },
      { name: 'Очищение', type: ServiceSiteType.cleansing },
    ],
  },
  { name: 'Дизайны', color: 'pink', image: servicesType9, type: ServiceSiteType.designs },
  { name: 'Доп. услуги', color: 'blue', image: servicesType8, type: ServiceSiteType.additional },
  { name: 'Брови и ресницы', color: 'pink', image: servicesType10, type: ServiceSiteType.brows },
  { name: 'Chevelure', color: 'pink', image: servicesType12, type: ServiceSiteType.chevelure },
]

/**
 * Which already selected types block a service of this type:
 * one manicure, one pedicure, and a complex replaces both
 */
const CONFLICTING_TYPES: Partial<Record<ServiceSiteType, ServiceSiteType[]>> = {
  [ServiceSiteType.manicure]: [ServiceSiteType.manicure, ServiceSiteType.complex],
  [ServiceSiteType.pedicure]: [ServiceSiteType.pedicure, ServiceSiteType.complex],
  [ServiceSiteType.complex]: [ServiceSiteType.manicure, ServiceSiteType.pedicure, ServiceSiteType.complex],
}

const getType = (service: CityService) => service.meta?.templateVars?.pageVars?.type

type ServiceListProps = {
  selectedServicesTypes?: number[],
  selectedServices?: Array<SelectedService<CityService>>,
  /** isSelected: the service was selected before the click (the click deselects it) */
  onServiceSelect?: (serviceId: number, amount: number, isSelected: boolean) => void,
  onServiceAmountChange?: (serviceId: number, amount: number, isSelected: boolean) => void,
  /** Another option of the same service page was picked while one of its options is selected */
  onServiceReplace?: (serviceId: number, newServiceId: number, amount: number) => void,
}

/** Services of the city (and of the booking salon, if chosen) grouped into collapsible categories */
export const ServiceList: React.FC<ServiceListProps> = (props) => {
  const {
    selectedServicesTypes = [], selectedServices = [], onServiceSelect, onServiceAmountChange, onServiceReplace,
  } = props
  const groups = useSelector(getActiveServiceGroups)
  const appointmentSalon = useSelector(getAppointmentSalon)
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null)
  const [expandedSubcategories, setExpandedSubcategories] = useState<Record<number, number | null>>({})

  const setExpandedSub = (parentIdx: number, subIdx: number | null) => {
    setExpandedSubcategories({ ...expandedSubcategories, [parentIdx]: subIdx })
  }

  const getGroupsByType = (type: ServiceSiteType) => groups.reduce<ServiceGroup[]>((acc, group) => {
    if (group.page.templateVars?.pageVars?.type !== type) {
      return acc
    }
    const options = appointmentSalon
      ? group.options.filter((option) => appointmentSalon.divisions.includes(option.service.division))
      : group.options
    if (options.length) {
      acc.push(options === group.options ? group : { ...group, options })
    }
    return acc
  }, [])

  const getSelectedServicesByType = (types: ServiceSiteType[]) => selectedServices.filter((service) => {
    const type = getType(service)
    return type !== undefined && types.includes(type)
  })

  const isDisabledByType = (type: ServiceSiteType | undefined, isSelected: boolean) => {
    const conflicts = type !== undefined ? CONFLICTING_TYPES[type] : undefined
    return !!conflicts && !isSelected && selectedServicesTypes.some((el) => conflicts.includes(el))
  }

  const renderGroup = (group: ServiceGroup) => {
    const selected = selectedServices.find((service) => (
      group.options.some((option) => option.service.id === service.id)
    ))
    return (
      <ServiceGroupListItem
        key={group.page.id}
        group={group}
        selected={selected}
        isButtonDisabled={isDisabledByType(group.page.templateVars?.pageVars?.type, !!selected)}
        salonId={appointmentSalon?.id}
        onServiceSelect={(serviceId, amount) => onServiceSelect?.(serviceId, amount, !!selected)}
        onServiceAmountChange={(serviceId, amount) => onServiceAmountChange?.(serviceId, amount, !!selected)}
        onServiceReplace={(serviceId, newServiceId, amount) => onServiceReplace?.(serviceId, newServiceId, amount)}
      />
    )
  }

  /** A selected service in a collapsed category */
  const renderSelectedService = (service: CityService) => {
    const isServiceSelected = selectedServices.some((selected) => selected.id === service.id)
    const isButtonDisabled = isDisabledByType(getType(service), isServiceSelected)

    return (
      <ServicesListItem
        key={service.id}
        service={service}
        isButtonDisabled={isButtonDisabled}
        isButtonSecondary={isServiceSelected}
        buttonTitle={isServiceSelected ? 'Отменить' : 'Выбрать'}
        isSelected={isServiceSelected}
        onButtonClick={(amount) => onServiceSelect?.(service.id, amount, isServiceSelected)}
        onAmountChange={(amount) => onServiceAmountChange?.(service.id, amount, isServiceSelected)}
        isShortView
        isAmountChangeable={!!service.isAmountChangeable}
        salonId={appointmentSalon?.id}
      />
    )
  }

  const categories = CATEGORIES.filter((category) => (
    category.subcategories ? category.subcategories.length > 0 : getGroupsByType(category.type).length > 0
  ))

  return (
    <div className={styles.container}>
      {categories.map((category, idx) => {
        const types = category.subcategories ? category.subcategories.map((sub) => sub.type) : [category.type]
        return (
          <ServicesCategory
            key={`category-${idx}`}
            name={category.name}
            image={category.image}
            isExpanded={expandedCategory === idx}
            onCollapse={() => setExpandedCategory(null)}
            onExpand={() => setExpandedCategory(idx)}
            expanded={category.subcategories
              ? category.subcategories.map((sub, subIdx) => (
                <ServicesSubCategory
                  key={`sub-${subIdx}`}
                  name={sub.name}
                  isExpanded={expandedSubcategories[idx] === subIdx}
                  onCollapse={() => setExpandedSub(idx, null)}
                  onExpand={() => setExpandedSub(idx, subIdx)}
                  expanded={getGroupsByType(sub.type).map(renderGroup)}
                  collapsed={getSelectedServicesByType([sub.type]).map(renderSelectedService)}
                />
              ))
              : getGroupsByType(category.type).map(renderGroup)}
            collapsed={getSelectedServicesByType(types).map(renderSelectedService)}
          />
        )
      })}
    </div>
  )
}
